import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { type BomRuleRow, computeRuleRowPreview } from "@/lib/bomRulePreview";
import { BOM_DEEP_LINK } from "@/lib/bomDeepLink";
import { BOM_TEST_SYNC_KEY, parseBomTestSync, writeBomTestSync } from "@/lib/bomTestSync";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { Card, CardBody } from "@/components/ui/Card";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import {
  BomTestPanel,
  type BomEditableColumnDef,
  type EditableKeyValueEditing,
  EditableKeyValueTable,
  RevisionLockedBanner,
  useBomMe,
} from "./BomShared";

const COL_RULE = [
  { key: "rule_description", label: "Description" },
  { key: "variable_name", label: "Variable" },
  { key: "condition_feature", label: "Condition feature" },
  { key: "condition_search", label: "Condition search" },
  { key: "replacement_sku_pattern", label: "Replacement pattern" },
  { key: "priority", label: "Priority" },
  { key: "multiplier", label: "Multiplier" },
] as const;

const PARSE_IGNORE_FOR_CONFIGURATION_HELP =
  "Check when this parse feature should not drive SDTestApp configuration or programming. Typical examples are accessories, in-box extras, kits, or customer-designated SKU tails that must not change firmware selection. Parsed feature names still participate in Bill of Materials rules. This flag also removes the feature from the manufacturing alternates comparison key. Leave unchecked for tokens that must influence device behavior or programming.";

const COL_PARSE: BomEditableColumnDef[] = [
  { key: "position", label: "Position" },
  { key: "name", label: "Name" },
  { key: "pattern", label: "Pattern" },
  { key: "multiple", label: "Multiple" },
  { key: "condition", label: "Condition" },
  { key: "strip", label: "Strip" },
  {
    key: "ignore_for_configuration",
    label: "Ignore for configuration",
    headerHelpText: PARSE_IGNORE_FOR_CONFIGURATION_HELP,
  },
];

type Tab = "parse" | "initial" | "rules";

function BomRulePreviewCell(props: {
  row: Record<string, unknown>;
  rows: Record<string, unknown>[];
  editing: EditableKeyValueEditing;
  testSku: string;
  initialRows: Record<string, unknown>[];
  parseRows: unknown[] | undefined;
  initialLoading: boolean;
  parseLoading: boolean;
  parseFailed: boolean;
}): ReactElement {
  if (props.initialLoading) {
    return <span className="text-slate-500">Loading…</span>;
  }
  if (props.parseLoading && (props.parseRows == null || props.parseRows.length === 0)) {
    return <span className="text-slate-500">Loading parse…</span>;
  }
  if (props.parseFailed) {
    return <span className="text-rose-700">Parse failed</span>;
  }
  const result = computeRuleRowPreview(Number(props.row.id), {
    testSku: props.testSku,
    initialRows: props.initialRows,
    rules: props.rows as BomRuleRow[],
    parseRows: props.parseRows,
    editing: props.editing,
  });
  switch (result.kind) {
    case "no_test_sku":
      return <span className="text-slate-500">Set a test SKU (right column or above).</span>;
    case "no_parse":
      return <span className="text-slate-500">No parse data. Wait for the test run or pick a SKU.</span>;
    case "no_initial":
      return <span className="text-slate-500">No initial BoM rows for this revision.</span>;
    case "invalid_regex":
      return <span className="text-rose-700 text-xs leading-snug">{result.message}</span>;
    case "no_target_variable":
      return (
        <span className="text-amber-800 text-xs leading-snug">
          No initial line for variable <code className="rounded bg-amber-100 px-0.5">{result.variableName}</code>
        </span>
      );
    case "ok":
      if (!result.conditionMet) {
        return (
          <div className="space-y-0.5 text-xs leading-snug">
            <span className="text-slate-600">Does not match</span>
            {result.featureSample ? (
              <div className="font-mono text-slate-500 break-all">Feature: {result.featureSample}</div>
            ) : null}
          </div>
        );
      }
      return (
        <div className="space-y-0.5 text-xs leading-snug">
          <span className="text-emerald-800">Matches</span>
          <div className="break-all font-mono text-slate-800">{result.expandedReplacement || "—"}</div>
        </div>
      );
    default:
      return <span className="text-slate-500">—</span>;
  }
}

function filterSelectOptions(options: SearchableSelectOption[], query: string): SearchableSelectOption[] {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) {
    return options;
  }
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(normalized) || option.value.toLowerCase().includes(normalized),
  );
}

function intFromSearchParam(value: string | null): number | "" {
  if (value == null || value === "") {
    return "";
  }
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : "";
}

export function SetBomRules() {
  const queryClient = useQueryClient();
  const meQuery = useBomMe();
  const me = meQuery.data;
  const hubWritesAllowed = useHubModuleWrite("bom");
  const bomReadOnlyTitle = "Your role has read-only access to the BoM management hub module.";
  const [searchParams, setSearchParams] = useSearchParams();

  const [templateId, setTemplateId] = useState<number | "">(() => intFromSearchParam(searchParams.get("templateId")));
  const [revision, setRevision] = useState<number | "">(() => intFromSearchParam(searchParams.get("revision")));
  const [testSku, setTestSku] = useState(() =>
    (searchParams.get(BOM_DEEP_LINK.testSku) ?? searchParams.get(BOM_DEEP_LINK.sku) ?? "").trim(),
  );
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const t = searchParams.get("tab");
    return t === "parse" || t === "initial" || t === "rules" ? t : "parse";
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [copyFromRevision, setCopyFromRevision] = useState<number | "">("");
  const [copyRevisionSearch, setCopyRevisionSearch] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [revisionSearch, setRevisionSearch] = useState("");
  const [showInPageTestColumn, setShowInPageTestColumn] = useState(true);

  const needSkuBomFocus =
    !searchParams.get(BOM_DEEP_LINK.templateId) &&
    Boolean(
      (searchParams.get(BOM_DEEP_LINK.sku) ?? searchParams.get(BOM_DEEP_LINK.testSku) ?? "").trim(),
    );
  const onlySkuParam = (searchParams.get(BOM_DEEP_LINK.sku) ?? searchParams.get(BOM_DEEP_LINK.testSku) ?? "").trim();

  const skuBomFocusQuery = useQuery({
    queryKey: ["bom", "sku-bom-focus", onlySkuParam],
    queryFn: () =>
      apiFetch<{
        templateId: number;
        revision: number;
        sku: string;
        fromApproved: boolean;
      }>("/api/bom/sku-bom-focus", { query: { sku: onlySkuParam } }),
    enabled: needSkuBomFocus && onlySkuParam.length > 0,
    retry: false,
    staleTime: 60_000,
  });

  const urlTemplate = searchParams.get(BOM_DEEP_LINK.templateId);
  const hasRevisionInUrl = searchParams.get(BOM_DEEP_LINK.revision) != null && searchParams.get(BOM_DEEP_LINK.revision) !== "";
  const parsedTemplateForLatest =
    urlTemplate != null && urlTemplate !== "" ? parseInt(urlTemplate, 10) : Number.NaN;
  const needLatestApprovedRevision = Number.isFinite(parsedTemplateForLatest) && !hasRevisionInUrl;

  const latestApprovedRevQuery = useQuery({
    queryKey: ["bom", "template-latest-approved", parsedTemplateForLatest],
    queryFn: () =>
      apiFetch<{ revision: number; fromApproved: boolean }>("/api/bom/template-latest-approved", {
        query: { templateId: parsedTemplateForLatest },
      }),
    enabled: needLatestApprovedRevision,
    retry: false,
    staleTime: 60_000,
  });

  const templatesQuery = useQuery({
    queryKey: ["bom", "templates"],
    queryFn: () =>
      apiFetch<{ templates: { label: string; value: number; controls_catalog_description?: boolean }[] }>(
        "/api/bom/templates",
      ),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });

  const kitStationsQuery = useQuery({
    queryKey: ["bom", "kit-stations"],
    queryFn: () => apiFetch<{ stations: { id: number; name: string }[] }>("/api/bom/kit-stations"),
    staleTime: 10 * 60_000,
  });

  const initialBoMColumns = useMemo((): BomEditableColumnDef[] => {
    const stationOptions = (kitStationsQuery.data?.stations ?? []).map((station) => ({
      value: String(station.id),
      label: station.name,
    }));
    return [
      { key: "variable_name", label: "Variable name", width: "32%" },
      { key: "quantity", label: "Quantity", width: "6rem" },
      { key: "component_sku", label: "Component SKU", width: "24%" },
      {
        key: "kit_to_station_id",
        label: "Kit station",
        width: "14rem",
        selectOptions: stationOptions,
        selectOptionsLoading: kitStationsQuery.isLoading,
        selectPanelMinWidth: 280,
      },
    ];
  }, [kitStationsQuery.data?.stations, kitStationsQuery.isLoading]);

  const revisionsQuery = useQuery({
    queryKey: ["bom", "revisions", templateId],
    queryFn: () =>
      apiFetch<{ revisions: Record<string, unknown>[] }>("/api/bom/template-revisions", {
        query: { templateId: templateId as number },
      }),
    enabled: templateId !== "",
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });

  const catalogDescriptionControlQuery = useQuery({
    queryKey: ["bom", "catalog-description-control", templateId],
    queryFn: () =>
      apiFetch<{
        template_id: number;
        template_name: string;
        controls_catalog_description: boolean;
      }>(`/api/bom/templates/${String(templateId)}/catalog-description-control`),
    enabled: templateId !== "",
    staleTime: 30_000,
  });

  const catalogDescriptionControlMutation = useMutation({
    mutationFn: (controlsCatalogDescription: boolean) =>
      apiFetch<{
        template_id: number;
        template_name: string;
        controls_catalog_description: boolean;
      }>(`/api/bom/templates/${String(templateId)}/catalog-description-control`, {
        method: "PATCH",
        body: { controls_catalog_description: controlsCatalogDescription },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bom", "catalog-description-control", templateId] });
      void queryClient.invalidateQueries({ queryKey: ["bom", "templates"] });
    },
  });

  const revisionMeta = revisionsQuery.data?.revisions.find(
    (r) => Number((r as { value: number }).value) === Number(revision),
  ) as { approved_at: string | null; submitted_at: string | null } | undefined;
  const approved = Boolean(revisionMeta?.approved_at);
  const submitted = Boolean(revisionMeta?.submitted_at);
  const isEditable = !approved && !submitted;
  const staffCanEdit = Boolean(me?.staff) && hubWritesAllowed;

  const templateOptions = useMemo(
    () =>
      (templatesQuery.data?.templates ?? []).map((templateRow) => ({
        value: String(templateRow.value),
        label: templateRow.label,
      })),
    [templatesQuery.data?.templates],
  );

  const revisionOptions = useMemo(
    () =>
      (revisionsQuery.data?.revisions ?? []).map((revisionRow) => {
        const row = revisionRow as { value: number; label: string };
        return { value: String(row.value), label: row.label };
      }),
    [revisionsQuery.data?.revisions],
  );

  const templateDropdownOptions = useMemo(() => {
    const none: SearchableSelectOption = { value: "", label: "Select…" };
    const filtered = filterSelectOptions(templateOptions, templateSearch);
    const query = templateSearch.trim().toLowerCase();
    if (query.length === 0 || none.label.toLowerCase().includes(query)) {
      return [none, ...filtered];
    }
    return filtered;
  }, [templateOptions, templateSearch]);

  const revisionDropdownOptions = useMemo(() => {
    if (templateId === "") {
      return [];
    }
    const none: SearchableSelectOption = { value: "", label: "Select…" };
    const filtered = filterSelectOptions(revisionOptions, revisionSearch);
    const query = revisionSearch.trim().toLowerCase();
    if (query.length === 0 || none.label.toLowerCase().includes(query)) {
      return [none, ...filtered];
    }
    return filtered;
  }, [revisionOptions, revisionSearch, templateId]);

  const copyRevisionDropdownOptions = useMemo(() => {
    const startEmpty: SearchableSelectOption = {
      value: "",
      label: "Start empty (next revision number)",
    };
    const filtered = filterSelectOptions(revisionOptions, copyRevisionSearch);
    const query = copyRevisionSearch.trim().toLowerCase();
    if (query.length === 0 || startEmpty.label.toLowerCase().includes(query)) {
      return [startEmpty, ...filtered];
    }
    return filtered;
  }, [revisionOptions, copyRevisionSearch]);

  const selectedTemplateLabel = useMemo(() => {
    if (templateId === "") {
      return null;
    }
    return (templatesQuery.data?.templates ?? []).find((templateRow) => templateRow.value === templateId)?.label ?? null;
  }, [templateId, templatesQuery.data?.templates]);

  const selectedRevisionLabel = useMemo(() => {
    if (revision === "") {
      return null;
    }
    return revisionOptions.find((option) => option.value === String(revision))?.label ?? null;
  }, [revision, revisionOptions]);

  const selectedCopyRevisionLabel = useMemo(() => {
    if (copyFromRevision === "") {
      return null;
    }
    return revisionOptions.find((option) => option.value === String(copyFromRevision))?.label ?? null;
  }, [copyFromRevision, revisionOptions]);

  const testSkusQuery = useQuery({
    queryKey: ["bom", "testSkus", templateId],
    queryFn: () =>
      apiFetch<{ skus: { value: string; label: string }[] }>("/api/bom/test-skus", {
        query: { templateId: templateId as number },
      }),
    enabled: templateId !== "",
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
  });

  // Only load row sets for the tab you are editing. The "initial" list is also needed for the in-page test
  // column (unused-variable check), and for the BoM rules "Preview" column on the rules tab.
  const hasTemplateRevision = templateId !== "" && revision !== "";
  const needInitialForTest = showInPageTestColumn;
  const needInitialForRulesPreview = activeTab === "rules";

  const initialQuery = useQuery({
    queryKey: ["bom", "rows", "initial", templateId, revision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/initial", {
        query: { templateId: templateId as number, revision: revision as number },
      }),
    enabled: hasTemplateRevision && (activeTab === "initial" || needInitialForTest || needInitialForRulesPreview),
    staleTime: 20_000,
    gcTime: 5 * 60_000,
  });

  const rulesQuery = useQuery({
    queryKey: ["bom", "rows", "rules", templateId, revision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/rules", {
        query: { templateId: templateId as number, revision: revision as number },
      }),
    enabled: hasTemplateRevision && activeTab === "rules",
    staleTime: 20_000,
    gcTime: 5 * 60_000,
  });

  const parseRowsQuery = useQuery({
    queryKey: ["bom", "rows", "skuParse", templateId, revision],
    queryFn: () =>
      apiFetch<{ rows: Record<string, unknown>[] }>("/api/bom/rows/sku-parse", {
        query: { templateId: templateId as number, revision: revision as number },
      }),
    enabled: hasTemplateRevision && activeTab === "parse",
    staleTime: 20_000,
    gcTime: 5 * 60_000,
  });

  useEffect(() => {
    const data = skuBomFocusQuery.data;
    if (!data) {
      return;
    }
    setTemplateId(data.templateId);
    setRevision(data.revision);
    setTestSku(data.sku);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(BOM_DEEP_LINK.templateId, String(data.templateId));
        next.set(BOM_DEEP_LINK.revision, String(data.revision));
        next.set(BOM_DEEP_LINK.testSku, data.sku);
        next.delete(BOM_DEEP_LINK.sku);
        if (!next.get(BOM_DEEP_LINK.tab)) {
          next.set(BOM_DEEP_LINK.tab, "parse");
        }
        return next;
      },
      { replace: true },
    );
  }, [skuBomFocusQuery.data, setSearchParams]);

  useEffect(() => {
    if (!needLatestApprovedRevision) {
      return;
    }
    const data = latestApprovedRevQuery.data;
    if (!data) {
      return;
    }
    setTemplateId(parsedTemplateForLatest);
    setRevision(data.revision);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(BOM_DEEP_LINK.revision, String(data.revision));
        return next;
      },
      { replace: true },
    );
  }, [latestApprovedRevQuery.data, needLatestApprovedRevision, parsedTemplateForLatest, setSearchParams]);

  useEffect(() => {
    setRevisionSearch("");
  }, [templateId]);

  useEffect(() => {
    if (modalOpen) {
      setCopyRevisionSearch("");
    }
  }, [modalOpen]);

  const testMutation = useMutation({
    mutationFn: async () => {
      if (templateId === "" || revision === "" || !testSku) {
        throw new Error("Select template, revision, and test SKU.");
      }
      const [parseResult, specResult] = await Promise.all([
        apiFetch<{ rows: unknown[] }>("/api/bom/preview/parse-sku-specific", {
          query: { sku: testSku, templateId, revision, useCache: "true" },
        }),
        apiFetch<{ rows: unknown[] }>("/api/bom/preview/bom-specific", {
          query: { sku: testSku, templateId, revision },
        }),
      ]);
      return { parse: parseResult, spec: specResult };
    },
  });

  const autoTestLastKey = useRef<string | null>(null);
  useEffect(() => {
    if (templateId === "" || revision === "" || !testSku.trim()) {
      autoTestLastKey.current = null;
      return;
    }
    const key = `${String(templateId)}|${String(revision)}|${testSku.trim()}`;
    if (autoTestLastKey.current === key) {
      return;
    }
    autoTestLastKey.current = key;
    testMutation.mutate();
  }, [templateId, revision, testSku, testMutation.mutate]);

  const newRevisionMutation = useMutation({
    mutationFn: async () => {
      if (templateId === "" || !newDescription.trim()) {
        throw new Error("Description is required.");
      }
      if (copyFromRevision === "") {
        return apiFetch<{ revision: { revision: number } }>("/api/bom/revisions", {
          method: "POST",
          body: { templateId, description: newDescription.trim() },
        });
      }
      return apiFetch<{ result: { new_revision?: unknown } }>("/api/bom/revisions/copy", {
        method: "POST",
        body: {
          templateId,
          description: newDescription.trim(),
          revisionToCopy: copyFromRevision,
        },
      });
    },
    onSuccess: (data) => {
      setModalOpen(false);
      setNewDescription("");
      setCopyFromRevision("");
      void queryClient.invalidateQueries({ queryKey: ["bom", "revisions", templateId] });
      if (data && typeof data === "object" && data !== null) {
        if ("revision" in data) {
          const row = (data as { revision: { revision: number } }).revision;
          if (typeof row?.revision === "number" && !Number.isNaN(row.revision)) {
            setRevision(row.revision);
          }
        }
        if ("newRevision" in data) {
          const n = (data as { newRevision: unknown }).newRevision;
          if (typeof n === "number" && !Number.isNaN(n)) {
            setRevision(n);
          }
        }
      }
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => {
      if (templateId === "" || revision === "") {
        throw new Error("Select a template and revision.");
      }
      return apiFetch("/api/bom/submit-revision", {
        method: "POST",
        body: { templateId, revision },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bom", "revisions", templateId] });
    },
  });

  const saveInitial = useMutation({
    mutationFn: (row: Record<string, unknown>) => {
      const id = row.id;
      if (id === undefined || id === null) {
        return apiFetch("/api/bom/rows/initial", { method: "POST", body: row });
      }
      return apiFetch(`/api/bom/rows/initial/${id}`, { method: "PUT", body: row });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bom", "rows", "initial", templateId, revision] });
    },
  });

  const saveRule = useMutation({
    mutationFn: (row: Record<string, unknown>) => {
      const id = row.id;
      if (id === undefined || id === null) {
        return apiFetch("/api/bom/rows/rule", { method: "POST", body: row });
      }
      return apiFetch(`/api/bom/rows/rule/${id}`, { method: "PUT", body: row });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bom", "rows", "rules", templateId, revision] });
    },
  });

  const saveParse = useMutation({
    mutationFn: (row: Record<string, unknown>) => {
      const id = row.id;
      if (id === undefined || id === null) {
        return apiFetch("/api/bom/rows/sku-parse", { method: "POST", body: row });
      }
      return apiFetch(`/api/bom/rows/sku-parse/${id}`, { method: "PUT", body: row });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bom", "rows", "skuParse", templateId, revision] });
    },
  });

  const parseQueryState = {
    data: testMutation.data?.parse,
    error: (testMutation.error as ApiError | null) ?? null,
    isFetching: testMutation.isPending,
  };
  const specificQueryState = {
    data: testMutation.data?.spec,
    error: (testMutation.error as ApiError | null) ?? null,
    isFetching: testMutation.isPending,
  };

  useEffect(() => {
    if (templateId === "" || revision === "") {
      return;
    }
    writeBomTestSync({ templateId, revision, testSku });
  }, [templateId, revision, testSku]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== BOM_TEST_SYNC_KEY || !event.newValue) {
        return;
      }
      const payload = parseBomTestSync(event.newValue);
      if (!payload) {
        return;
      }
      setTemplateId(payload.templateId);
      setRevision(payload.revision);
      setTestSku(payload.testSku);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const openTestInNewWindow = (): void => {
    if (templateId === "" || revision === "") {
      return;
    }
    const params = new URLSearchParams({
      [BOM_DEEP_LINK.templateId]: String(templateId),
      [BOM_DEEP_LINK.revision]: String(revision),
    });
    if (testSku.trim()) {
      params.set(BOM_DEEP_LINK.testSku, testSku.trim());
    }
    const child = window.open(
      `${window.location.origin}/bom/test-revision?${params.toString()}`,
      "bom-test-revision",
      "noopener,noreferrer",
    );
    if (child) {
      setShowInPageTestColumn(false);
      writeBomTestSync({ templateId, revision, testSku });
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Set BoM rules</h1>
        <p className="text-sm text-slate-600">
          Choose a template and draft revision, edit initial BoM, rules, and parse steps, then submit for approval. Links
          that include a <code className="rounded bg-slate-100 px-1 text-xs">sku</code> (without a template) load the
          right template and latest approved revision automatically.
        </p>
        {(needSkuBomFocus && skuBomFocusQuery.isPending) || (needLatestApprovedRevision && latestApprovedRevQuery.isPending) ? (
          <p className="text-sm text-slate-500">Resolving template and revision from the link…</p>
        ) : null}
        {needSkuBomFocus && skuBomFocusQuery.isError ? (
          <p className="mt-1 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {(skuBomFocusQuery.error as Error).message}
          </p>
        ) : null}
        {needLatestApprovedRevision && latestApprovedRevQuery.isError && !needSkuBomFocus ? (
          <p className="mt-1 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {(latestApprovedRevQuery.error as Error).message}
          </p>
        ) : null}
      </header>

      {!me?.staff ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          No <code className="text-xs">sgs.staff</code> record for your sign-in email. You can open templates, but
          creating revisions, saving edits, and submitting require an active staff row for your address.
        </p>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Template</Label>
          <SearchableSelect
            className="mt-1"
            value={templateId === "" ? "" : String(templateId)}
            onValueChange={(option) => {
              if (!option || option.value === "") {
                setTemplateId("");
                setRevision("");
                setTestSku("");
                setTemplateSearch("");
                return;
              }
              setTemplateId(Number(option.value));
              setRevision("");
              setTestSku("");
              setTemplateSearch("");
            }}
            options={templateDropdownOptions}
            searchValue={templateSearch}
            onSearchChange={setTemplateSearch}
            placeholder="Select…"
            searchPlaceholder="Search templates…"
            emptyMessage="No templates match."
            isLoading={templatesQuery.isLoading}
            selectedLabelOverride={selectedTemplateLabel}
          />
        </div>
        <div>
          <Label>Revision</Label>
          <SearchableSelect
            className="mt-1"
            value={revision === "" ? "" : String(revision)}
            onValueChange={(option) => {
              if (!option || option.value === "") {
                setRevision("");
                setRevisionSearch("");
                return;
              }
              setRevision(Number(option.value));
              setRevisionSearch("");
            }}
            options={revisionDropdownOptions}
            searchValue={revisionSearch}
            onSearchChange={setRevisionSearch}
            placeholder="Select…"
            searchPlaceholder="Search revisions…"
            emptyMessage="No revisions match."
            isLoading={revisionsQuery.isLoading}
            disabled={templateId === ""}
            selectedLabelOverride={selectedRevisionLabel}
          />
        </div>
      </div>

      {templateId !== "" && revision !== "" ? <RevisionLockedBanner approved={approved} submitted={submitted} /> : null}

      {templateId !== "" ? (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle}>
            <label className="inline-flex cursor-pointer items-start gap-2 text-sm text-slate-800">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300"
                checked={catalogDescriptionControlQuery.data?.controls_catalog_description === true}
                disabled={
                  !hubWritesAllowed ||
                  catalogDescriptionControlQuery.isLoading ||
                  catalogDescriptionControlMutation.isPending
                }
                onChange={(event) => {
                  catalogDescriptionControlMutation.mutate(event.target.checked);
                }}
              />
              <span>
                <span className="font-medium">Control catalog description from BoM rules</span>
                <span className="mt-0.5 block text-slate-600">
                  When enabled, each SKU on this template keeps Megaventory description and variant in sync with{" "}
                  <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">generate_description_parts</code>.
                  Manual edits on the SKU page are blocked. Turning this on queues an immediate sync for linked SKUs.
                </span>
              </span>
            </label>
          </ReadOnlyHoverLayer>
          {catalogDescriptionControlMutation.isError ? (
            <p className="mt-2 text-sm text-rose-700">{(catalogDescriptionControlMutation.error as Error).message}</p>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setModalOpen(true)}
            disabled={templateId === "" || !me?.staff || !hubWritesAllowed}
          >
            New revision
          </Button>
        </ReadOnlyHoverLayer>
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle}>
          <Button
            type="button"
            size="sm"
            onClick={() => submitMutation.mutate()}
            disabled={
              templateId === "" ||
              revision === "" ||
              !isEditable ||
              submitMutation.isPending ||
              !me?.staff ||
              !hubWritesAllowed
            }
          >
            {submitMutation.isPending ? "Submitting…" : "Submit for approval"}
          </Button>
        </ReadOnlyHoverLayer>
        {submitMutation.isError ? (
          <span className="text-sm text-rose-700">{(submitMutation.error as Error).message}</span>
        ) : null}
        {submitMutation.isSuccess ? <span className="text-sm text-emerald-700">Submitted.</span> : null}
      </div>

      {templateId !== "" && revision !== "" ? (
        <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-2">
          {(
            [
              ["parse", "Parse"],
              ["initial", "Initial BoM"],
              ["rules", "BoM rules"],
            ] as const
          ).map(([id, label]) => (
            <button
              type="button"
              key={id}
              onClick={() => setActiveTab(id)}
              className={
                activeTab === id
                  ? "rounded-md bg-brand-50 px-3 py-2 text-base font-medium text-brand-700"
                  : "rounded-md px-3 py-2 text-base text-slate-600 hover:bg-slate-100"
              }
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {templateId !== "" && revision !== "" ? (
        <div className="space-y-3">
          <div className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={showInPageTestColumn}
                  onChange={(event) => setShowInPageTestColumn(event.target.checked)}
                />
                Show in-page test column
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="md"
                variant="secondary"
                onClick={openTestInNewWindow}
                className="inline-flex items-center gap-1.5"
              >
                <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                Open tests in new window
              </Button>
            </div>
            {!showInPageTestColumn ? (
              <p className="w-full text-sm text-slate-600">
                Tests are in the other window only. It stays linked to this page (template, revision, test SKU) while both
                are open.
              </p>
            ) : null}
          </div>

          <div
            className={
              showInPageTestColumn
                ? "grid grid-cols-1 gap-6 xl:grid-cols-12 xl:items-start 2xl:gap-8"
                : "grid grid-cols-1"
            }
          >
            <div className={showInPageTestColumn ? "min-w-0 xl:col-span-7 2xl:col-span-8" : "min-w-0"}>
              {activeTab === "parse" ? (
                <EditableKeyValueTable
                  title="SKU parse steps"
                  rows={parseRowsQuery.data?.rows ?? []}
                  columns={[...COL_PARSE]}
                  isEditable={isEditable && staffCanEdit}
                  density="comfortable"
                  newRowTemplate={{
                    template_id: templateId,
                    revision: revision,
                    position: 0,
                    name: "",
                    pattern: "",
                    multiple: false,
                    condition: "",
                    strip: false,
                  }}
                  onSave={async (row) => {
                    await saveParse.mutateAsync(row);
                  }}
                  onAdd={async (draft) => {
                    await saveParse.mutateAsync(draft);
                  }}
                />
              ) : null}

              {activeTab === "initial" ? (
                <EditableKeyValueTable
                  title="Initial BoM (template variable lines)"
                  rows={initialQuery.data?.rows ?? []}
                  columns={initialBoMColumns}
                  isEditable={isEditable && staffCanEdit}
                  density="comfortable"
                  newRowTemplate={{
                    template_id: templateId,
                    revision: revision,
                    variable_name: "",
                    quantity: 0,
                    component_sku: "",
                    kit_to_station_id: null,
                  }}
                  onSave={async (row) => {
                    await saveInitial.mutateAsync(row);
                  }}
                  onAdd={async (draft) => {
                    await saveInitial.mutateAsync(draft);
                  }}
                />
              ) : null}

              {activeTab === "rules" ? (
                <EditableKeyValueTable
                  title="BoM rules"
                  rows={rulesQuery.data?.rows ?? []}
                  columns={[...COL_RULE]}
                  isEditable={isEditable && staffCanEdit}
                  density="comfortable"
                  newRowTemplate={{
                    template_id: templateId,
                    revision: revision,
                    rule_description: "",
                    variable_name: "",
                    condition_feature: "",
                    condition_search: "",
                    replacement_sku_pattern: "{existing}",
                    priority: 0,
                    multiplier: 1,
                  }}
                  onSave={async (row) => {
                    await saveRule.mutateAsync(row);
                  }}
                  onAdd={async (draft) => {
                    await saveRule.mutateAsync(draft);
                  }}
                  trailingColumn={{
                    label: "Preview",
                    render: (context) => (
                      <BomRulePreviewCell
                        row={context.row}
                        rows={context.rows}
                        editing={context.editing}
                        testSku={testSku}
                        initialRows={initialQuery.data?.rows ?? []}
                        parseRows={testMutation.data?.parse?.rows}
                        initialLoading={initialQuery.isLoading}
                        parseLoading={testMutation.isPending}
                        parseFailed={testMutation.isError}
                      />
                    ),
                  }}
                />
              ) : null}
            </div>

            {showInPageTestColumn ? (
              <div className="min-w-0 border-t border-slate-200 pt-4 xl:sticky xl:top-4 xl:col-span-5 xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto 2xl:col-span-4 xl:border-slate-200 xl:border-t-0 xl:border-l xl:pl-6 xl:pt-0">
                <h2 className="mb-2 text-base font-semibold text-slate-800">Test revision (in page)</h2>
                <p className="mb-3 text-sm leading-relaxed text-slate-600">
                  Wider layout and larger type on this screen. The right column is sticky: scroll the left table, or scroll
                  inside this column when test output is long. Use a second window for a full-width test view.
                </p>
                <BomTestPanel
                  testSku={testSku}
                  onTestSku={setTestSku}
                  onRunTest={() => {
                    if (!hubWritesAllowed) return;
                    testMutation.mutate();
                  }}
                  running={testMutation.isPending}
                  skuOptions={testSkusQuery.data?.skus ?? []}
                  parseQuery={parseQueryState}
                  specificQuery={specificQueryState}
                  initialRows={initialQuery.data?.rows}
                  density="comfortable"
                  runTestBlocked={!hubWritesAllowed}
                  runTestBlockedTitle={bomReadOnlyTitle}
                />
                {testMutation.isError ? (
                  <p className="mt-2 text-base text-rose-700">{(testMutation.error as Error).message}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true">
          <Card className="w-full max-w-md shadow-lg">
            <CardBody className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-900">New draft revision</h2>
              <div>
                <Label>Description</Label>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle} layout="block">
                  <Input
                    value={newDescription}
                    disabled={!hubWritesAllowed}
                    onChange={(e) => setNewDescription(e.target.value)}
                  />
                </ReadOnlyHoverLayer>
              </div>
              <div>
                <Label>Copy rules from revision (optional)</Label>
                <SearchableSelect
                  className="mt-1"
                  value={copyFromRevision === "" ? "" : String(copyFromRevision)}
                  disabled={!hubWritesAllowed}
                  onValueChange={(option) => {
                    if (!option || option.value === "") {
                      setCopyFromRevision("");
                      setCopyRevisionSearch("");
                      return;
                    }
                    setCopyFromRevision(Number(option.value));
                    setCopyRevisionSearch("");
                  }}
                  options={copyRevisionDropdownOptions}
                  searchValue={copyRevisionSearch}
                  onSearchChange={setCopyRevisionSearch}
                  placeholder="Start empty (next revision number)"
                  searchPlaceholder="Search revisions…"
                  emptyMessage="No revisions match."
                  isLoading={revisionsQuery.isLoading}
                  selectedLabelOverride={selectedCopyRevisionLabel}
                />
              </div>
              {newRevisionMutation.isError ? (
                <p className="text-sm text-rose-700">{(newRevisionMutation.error as Error).message}</p>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={bomReadOnlyTitle}>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!hubWritesAllowed) return;
                      newRevisionMutation.mutate();
                    }}
                    disabled={!newDescription.trim() || newRevisionMutation.isPending || !hubWritesAllowed}
                  >
                    {newRevisionMutation.isPending ? "Creating…" : "Create"}
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
